import { Navigate, Route, Routes, useLocation, useParams } from 'react-router';
import Layout from './components/Layout';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { BannersPage } from './pages/BannersPage';
import CreateCurrencyPage from './pages/CreateCurrencyPage';
import CreatePlanPage from './pages/CreatePlanPage';
import CreateTierPage from './pages/CreateTierPage';
import DashboardPage from './pages/DashboardPage';
import EditCurrencyPage from './pages/EditCurrencyPage';
import EditPlanPage from './pages/EditPlanPage';
import EditTierPage from './pages/EditTierPage';
import IssueTicketPage from './pages/IssueTicketPage';
import LoginPage from './pages/LoginPage';
import { NotificationsPage } from './pages/NotificationsPage';
import OgpPage from './pages/OgpPage';
import PasskeysPage from './pages/PasskeysPage';
import PlansPage from './pages/PlansPage';
import PostDetailPage from './pages/PostDetailPage';
import PostsPage from './pages/PostsPage';
import RegisterPasskeyPage from './pages/RegisterPasskeyPage';
import SchedulerSessionsPage from './pages/SchedulerSessionsPage';
import ServiceSettingsPage from './pages/ServiceSettingsPage';
import TicketDetailPage from './pages/TicketDetailPage';
import TicketsPage from './pages/TicketsPage';
import TiersPage from './pages/TiersPage';
import UserDetailPage from './pages/UserDetailPage';
import UserStatusLogsPage from './pages/UserStatusLogsPage';
import UsersPage from './pages/UsersPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className='flex min-h-screen items-center justify-center'>
        <div className='text-lg'>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to='/login' replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route
        path='/login'
        element={isAuthenticated ? <Navigate to='/' replace /> : <LoginPage />}
      />
      <Route path='/passkey/register' element={<RegisterPasskeyPage />} />
      <Route
        path='/'
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path='users' element={<UsersPage />} />
        <Route path='users/:userId' element={<UserDetailPage />} />
        <Route path='users/:userId/status-logs' element={<UserStatusLogsPage />} />
        <Route path='reserved-actions' element={<PostsPage />} />
        <Route path='reserved-actions/:postId' element={<PostDetailPage />} />
        {/* 旧パスからのリダイレクト（後方互換）。既存のリンクやブックマークを壊さない */}
        <Route path='posts' element={<LegacyPostsRedirect />} />
        <Route path='posts/:postId' element={<LegacyPostDetailRedirect />} />
        <Route path='tickets' element={<TicketsPage />} />
        <Route path='tickets/issue' element={<IssueTicketPage />} />
        <Route path='tickets/:ticketId' element={<TicketDetailPage />} />
        <Route path='tiers' element={<TiersPage />} />
        <Route path='tiers/create' element={<CreateTierPage />} />
        <Route path='tiers/:tierId/edit' element={<EditTierPage />} />
        <Route path='plans' element={<PlansPage />} />
        <Route path='plans/create' element={<CreatePlanPage />} />
        <Route path='plans/:planId/edit' element={<EditPlanPage />} />
        <Route path='settings' element={<ServiceSettingsPage />} />
        <Route path='settings/currencies/create' element={<CreateCurrencyPage />} />
        <Route path='settings/currencies/:currencyId/edit' element={<EditCurrencyPage />} />
        <Route path='scheduler-sessions' element={<SchedulerSessionsPage />} />
        <Route path='notifications' element={<NotificationsPage />} />
        <Route path='banners' element={<BannersPage />} />
        <Route path='ogp' element={<OgpPage />} />
        <Route path='passkeys' element={<PasskeysPage />} />
      </Route>
    </Routes>
  );
}

/**
 * 旧パス `/posts` から新パス `/reserved-actions` へのリダイレクト。
 * `?userId=` などのクエリを保持したまま遷移させる。
 */
function LegacyPostsRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/reserved-actions${search}`} replace />;
}

/**
 * 旧パス `/posts/:postId` から新パス `/reserved-actions/:postId` へのリダイレクト。
 * ID を保持したまま遷移させ、既存のリンクやブックマークを壊さない。
 */
function LegacyPostDetailRedirect() {
  const { postId } = useParams();
  return <Navigate to={`/reserved-actions/${postId ?? ''}`} replace />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
